import React, { useEffect, useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  defs,
  linearGradient,
  stop
} from 'recharts';
import { 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  AlertCircle, 
  RefreshCw,
  Users,
  Activity
} from 'lucide-react';

// --- Types & Interfaces (implied for JS) ---
// If using TypeScript, we would define interfaces for PageData, ChartData, etc.

// --- Mock Admin Component (Placeholder for your existing import) ---
const AdminOnly = () => (
  <div className="flex h-screen items-center justify-center bg-slate-900 text-white">
    <div className="text-center">
      <h2 className="text-3xl font-bold text-red-500">Access Restricted</h2>
      <p className="text-slate-400">Admin privileges required.</p>
    </div>
  </div>
);

// --- 1. Utility Components ---

/** * Reusable Card Component with Glassmorphism 
 */
const Card = ({ children, className = '' }) => (
  <div className={`bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden ${className}`}>
    {children}
  </div>
);

/** * Skeleton Loader for better UX during data fetch 
 */
const DashboardSkeleton = () => (
  <div className="min-h-screen bg-slate-950 p-6 animate-pulse">
    <div className="h-12 w-64 bg-slate-800 rounded-lg mx-auto mb-8"></div>
    <div className="h-12 w-96 bg-slate-800 rounded-full mx-auto mb-12"></div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {[1, 2].map((i) => (
        <div key={i} className="h-96 bg-slate-800/50 rounded-2xl"></div>
      ))}
    </div>
  </div>
);

/** * Custom Tooltip for Recharts 
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/90 border border-slate-700 p-4 rounded-xl shadow-2xl backdrop-blur-md">
        <p className="text-slate-400 text-sm mb-1">{label}</p>
        <p className="text-emerald-400 font-bold text-lg flex items-center gap-2">
          <Users size={16} />
          {payload[0].value.toLocaleString()} 
          <span className="text-slate-500 text-xs font-normal">Visits</span>
        </p>
      </div>
    );
  }
  return null;
};

// --- 2. Custom Hooks ---

/**
 * Hook to handle API logic, state, and errors
 */
const useAnalytics = (apiEndpoint) => {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, admin_restricted
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchData = async () => {
    setStatus('loading');
    setErrorMessage(null);
    try {
      const response = await fetch(`${apiEndpoint}/visits`, {
        credentials: 'include',
      });

      if (response.status === 403) {
        setStatus('admin_restricted');
        return;
      }

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const result = await response.json();
      // Ensure we safeguard against malformed API responses
      setData(result.pageStats || []); 
      setStatus('success');
    } catch (err) {
      console.error('Analytics Fetch Error:', err);
      setErrorMessage(err.message || 'Failed to load analytics.');
      setStatus('error');
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiEndpoint]);

  return { data, status, errorMessage, retry: fetchData };
};

// --- 3. Sub-Components ---

const StatBadge = ({ label, value, icon: Icon, colorClass }) => (
  <div className="flex flex-col items-center justify-center p-3 bg-slate-800/50 rounded-xl border border-slate-700/30">
    <div className={`flex items-center gap-2 text-sm font-medium ${colorClass} mb-1`}>
      <Icon size={14} />
      {label}
    </div>
    <div className="text-xl font-bold text-slate-100">{value}</div>
  </div>
);

const ChartSection = ({ pageData, viewMode }) => {
  // Memoize calculations to avoid re-running on every render
  const { chartData, stats } = useMemo(() => {
    let rawData = [];
    
    // Normalize Data based on view
    if (viewMode === 'daily') {
      rawData = pageData.dailyVisits.map(([d, v]) => ({ date: d, visits: v }));
    } else if (viewMode === 'weekly') {
      rawData = pageData.weeklyVisits.map(([w, v]) => ({ date: `Week ${w}`, visits: v }));
    } else if (viewMode === 'monthly') {
      rawData = pageData.monthlyVisits.map(([m, v]) => ({ date: m, visits: v }));
    }

    // Calculate Stats
    const total = pageData.totalVisits || 0;
    const visitsArr = rawData.map(d => d.visits);
    const avg = visitsArr.length ? Math.round(total / visitsArr.length) : 0;
    const max = visitsArr.length ? Math.max(...visitsArr) : 0;

    return { 
      chartData: rawData, 
      stats: { total, avg, max } 
    };
  }, [pageData, viewMode]);

  return (
    <Card className="hover:border-indigo-500/30 transition-colors duration-500">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400 flex items-center gap-2">
            <Activity size={24} className="text-indigo-400" />
            {pageData.pageType.toUpperCase()}
          </h2>
          
          {/* Summary Stats Grid */}
          <div className="grid grid-cols-3 gap-3 w-full sm:w-auto">
            <StatBadge label="Total" value={stats.total.toLocaleString()} icon={Users} colorClass="text-blue-400" />
            <StatBadge label="Avg" value={stats.avg.toLocaleString()} icon={BarChart3} colorClass="text-purple-400" />
            <StatBadge label="Peak" value={stats.max.toLocaleString()} icon={TrendingUp} colorClass="text-emerald-400" />
          </div>
        </div>

        {/* Chart Area */}
        <div className="h-[300px] w-full bg-gradient-to-b from-slate-900/50 to-transparent rounded-xl p-2 border border-slate-800">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`gradient-${pageData.pageType}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis 
                dataKey="date" 
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                dy={10}
                minTickGap={30}
              />
              <YAxis 
                tick={{ fill: '#64748b', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(1)}k` : val}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="visits" 
                stroke="#818cf8" 
                strokeWidth={3}
                fillOpacity={1} 
                fill={`url(#gradient-${pageData.pageType})`} 
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};

// --- 4. Main Component ---

const Analytics = () => {
  const [viewMode, setViewMode] = useState('daily');
  const API = process.env.REACT_APP_API || 'http://localhost:3000'; // Fallback for safety
  const { data: analyticsData, status, errorMessage, retry } = useAnalytics(API);

  // --- View Logic ---

  if (status === 'loading') return <DashboardSkeleton />;
  if (status === 'admin_restricted') return <AdminOnly />;
  
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
        <div className="bg-red-950/30 border border-red-900/50 p-8 rounded-2xl text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-red-200 mb-2">Failed to Load Analytics</h3>
          <p className="text-red-300/70 mb-6">{errorMessage}</p>
          <button 
            onClick={retry}
            className="flex items-center justify-center gap-2 mx-auto bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-full transition-all"
          >
            <RefreshCw size={18} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // --- Main Render ---

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-indigo-500/30">
      
      {/* Background decoration */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-900/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-900/20 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 lg:px-8 max-w-7xl">
        
        {/* Header Section */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
          <div className="text-center md:text-left">
            <h1 className="text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-400">
              Analytics Dashboard
            </h1>
            <p className="text-slate-500 mt-2 text-sm font-medium uppercase tracking-wider">
              Real-time Traffic Overview
            </p>
          </div>

          {/* View Toggles */}
          <div className="bg-slate-900/80 p-1.5 rounded-xl border border-slate-800 flex shadow-lg backdrop-blur-sm">
            {['daily', 'weekly', 'monthly'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`
                  relative px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-300 capitalize
                  ${viewMode === mode 
                    ? 'text-white shadow-md' 
                    : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}
                `}
              >
                {/* Active Tab Background Animation */}
                {viewMode === mode && (
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg -z-10" />
                )}
                {mode}
              </button>
            ))}
          </div>
        </header>

        {/* Empty State Check */}
        {analyticsData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-600">
            <Calendar size={64} className="mb-4 opacity-50" />
            <p className="text-lg">No analytics data available for this period.</p>
          </div>
        ) : (
          /* Charts Grid */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {analyticsData.map((page, index) => (
              <ChartSection 
                key={page.pageType || index} 
                pageData={page} 
                viewMode={viewMode} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
